<template>
  <Teleport to="body">
  <section ref="dialog" class="graph-modal" role="dialog" aria-modal="true" aria-labelledby="dataset-graph-title" tabindex="-1" @keydown="handleKeydown">
    <header class="graph-header">
      <div class="graph-heading"><span class="graph-icon"><i class="bi bi-graph-up-arrow" aria-hidden="true"></i></span><div><h2 id="dataset-graph-title">{{ title || 'Dataset graph' }}</h2><p>{{ rows.length.toLocaleString() }} rows · Current page only</p></div></div>
      <div class="header-actions">
      <div class="chart-type-toggle">
        <button v-for="type in ['line', 'scatter']" :key="type" type="button" class="chart-type-btn"
          :class="{ active: chartType === type }" :aria-pressed="chartType === type" @click="chartType = type">{{ type }}</button>
      </div>
      <button ref="closeButton" class="close-graph" type="button" @click="$emit('close')" aria-label="Close graph and return to rows"><i class="bi bi-x-lg" aria-hidden="true"></i><span>Close</span><kbd>Esc</kbd></button>
      </div>
    </header>
    <div class="graph-workspace">
    <div class="graph-controls">
      <div class="controls-heading"><h3>Build your chart</h3><p>Choose an X axis, then the measurements to compare.</p></div>
      <label class="axis-block">X axis <small>Horizontal position</small>
        <select v-model="xAxis" class="axis-select">
          <option value="">Row ordinal</option>
          <option v-for="column in xColumns" :key="column.column_key" :value="column.column_key">{{ label(column) }}</option>
        </select>
      </label>
      <fieldset class="y-block">
        <legend>Y axis <span>{{ ySeries.length }} selected</span></legend>
        <input v-model="seriesSearch" class="series-search" type="search" placeholder="Find a measurement…" aria-label="Search Y-axis measurements" />
        <div class="series-actions"><button type="button" @click="ySeries = []">Clear selection</button></div>
        <p v-if="!numericColumns.length" class="controls-hint">No numeric columns are available on this page.</p>
        <div class="y-series-list">
          <label v-for="column in filteredNumericColumns" :key="column.column_key" class="y-series-chip" :class="{ selected: ySeries.includes(column.column_key) }">
            <input v-model="ySeries" type="checkbox" :value="column.column_key">
            <span class="series-dot" :style="{ background: palette[numericColumns.indexOf(column) % palette.length] }"></span><span>{{ label(column) }}</span>
          </label>
        </div>
        <p v-if="numericColumns.length && !filteredNumericColumns.length" class="controls-hint">No measurements match your search.</p>
      </fieldset>
    </div>
    <div class="graph-stage">
    <p v-if="hasDecimal" class="graph-note">Decimal columns are excluded from charts until exact-precision plotting is supported. Table text is unchanged.</p>
    <p v-if="timeAxis" class="graph-note">Dates use their supplied offset; datetimes without an offset use browser-local time. No timezone is inferred.</p>
    <div class="graph-body">
      <canvas v-show="rows.length && ySeries.length" ref="canvas" role="img" aria-label="Selected numeric series from the current page"></canvas>
      <div v-if="!rows.length || !ySeries.length" class="graph-empty"><i class="bi bi-bar-chart" aria-hidden="true"></i><h3>{{ rows.length ? 'Choose a measurement' : 'No rows on this page' }}</h3><p>{{ rows.length ? 'Select a Y-axis measurement in the sidebar to start plotting.' : 'Close the graph and query a page containing data.' }}</p></div>
    </div>
    <p v-if="rows.length && ySeries.length && !plottedPoints && !chartError" class="graph-note">No valid points for these axes. Try another measurement or X axis.</p>
    <footer class="graph-footer">{{ rows.length }} page rows · {{ plottedPoints }} valid plotted points. Current bounded page only; no dataset-wide aggregation.
      Null, missing, unsafe integers and non-finite values are skipped. Line segments follow page order, not a server-side sort.
      <span v-if="chartError" role="alert">{{ chartError }}</span>
    </footer>
    </div>
    </div>
  </section>
  </Teleport>
</template>

<script>
import { Chart, LineController, ScatterController, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import { columnLabel, chartPoints } from '@/utils/tabularRows';

Chart.register(LineController, ScatterController, LinearScale, PointElement, LineElement, Tooltip, Legend);
const PALETTE = ['#18392B', '#2196F3', '#FF9800', '#E91E63', '#9C27B0', '#00BCD4', '#FF5722', '#607D8B', '#8BC34A', '#FFC107', '#795548', '#3F51B5'];

export default {
  name: 'TabularDatasetGraph',
  emits: ['close'],
  props: {
    title: { type: String, default: '' },
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
  },
  data() {
    return { xAxis: '', ySeries: [], seriesSearch: '', chartType: 'line', palette: PALETTE, plottedPoints: 0, chartError: '' };
  },
  computed: {
    numericColumns() { return this.columns.filter(column => ['integer', 'float'].includes(column.logical_type)); },
    filteredNumericColumns() { const search = this.seriesSearch.trim().toLowerCase(); return this.numericColumns.filter(column => columnLabel(column).toLowerCase().includes(search)); },
    xColumns() { return this.columns.filter(column => ['integer', 'float', 'date', 'datetime'].includes(column.logical_type)); },
    xColumn() { return this.xColumns.find(column => column.column_key === this.xAxis); },
    timeAxis() { return ['date', 'datetime'].includes(this.xColumn?.logical_type); },
    hasDecimal() { return this.columns.some(column => column.logical_type === 'decimal'); },
  },
  watch: {
    columns: { deep: true, handler() { this.resetAxes(); } },
    rows() { this.scheduleChart(); },
    xAxis() { this.scheduleChart(); },
    ySeries: { deep: true, handler() { this.scheduleChart(); } },
    chartType() { this.scheduleChart(); },
  },
  created() { this.chartInstance = null; this.chartDisposed = false; this.chartGeneration = 0; },
  mounted() {
    this.previousFocus = document.activeElement;
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timeColumn = this.xColumns.find(column => ['date', 'datetime'].includes(column.logical_type));
    if (timeColumn) this.xAxis = timeColumn.column_key;
    this.resetAxes();
    this.$nextTick(() => this.$refs.closeButton?.focus());
  },
  beforeUnmount() {
    this.chartDisposed = true; ++this.chartGeneration; this.destroyChart();
    document.body.style.overflow = this.previousOverflow;
    if (this.previousFocus?.isConnected) this.previousFocus.focus();
  },
  methods: {
    label: columnLabel,
    handleKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.$emit('close'); return; }
      if (event.key !== 'Tab') return;
      const controls = Array.from(this.$refs.dialog.querySelectorAll('button:not(:disabled), select:not(:disabled), input:not(:disabled), [tabindex="0"]')).filter(element => element.getClientRects().length);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    },
    resetAxes() {
      if (!this.xColumns.some(column => column.column_key === this.xAxis)) this.xAxis = '';
      this.ySeries = this.ySeries.filter(key => this.numericColumns.some(column => column.column_key === key));
      if (!this.ySeries.length) this.ySeries = this.numericColumns.slice(0, 3).map(column => column.column_key);
      this.scheduleChart();
    },
    scheduleChart() {
      const generation = ++this.chartGeneration;
      this.$nextTick(() => { if (!this.chartDisposed && generation === this.chartGeneration) this.rebuildChart(); });
    },
    destroyChart() {
      if (this.chartInstance) { this.chartInstance.destroy(); this.chartInstance = null; }
    },
    rebuildChart() {
      this.destroyChart();
      this.plottedPoints = 0;
      this.chartError = '';
      if (!this.$refs.canvas || !this.rows.length || !this.ySeries.length) return;
      const scatter = this.chartType === 'scatter';
      const datasets = this.ySeries.map(key => {
        const column = this.numericColumns.find(item => item.column_key === key);
        const color = this.palette[this.numericColumns.indexOf(column) % this.palette.length];
        const data = chartPoints(this.rows, this.xColumn, column);
        this.plottedPoints += data.length;
        return { label: columnLabel(column), data, borderColor: color, backgroundColor: color + (scatter ? '99' : '28'),
          pointRadius: scatter ? 4 : 2, pointHoverRadius: 6, borderWidth: scatter ? 0 : 2,
          showLine: !scatter, fill: false, tension: 0.25 };
      });
      const timeAxis = this.timeAxis;
      try {
        this.chartInstance = new Chart(this.$refs.canvas, {
          type: this.chartType, data: { datasets },
          options: {
            responsive: true, maintainAspectRatio: false, animation: false,
            plugins: {
              legend: { labels: { usePointStyle: true, color: '#333', padding: 20 } },
              tooltip: { backgroundColor: 'rgba(18,40,28,0.93)', titleColor: '#cdfae1', bodyColor: '#e8f5e9', padding: 12,
                callbacks: { title: items => timeAxis ? new Date(items[0].raw.x).toLocaleString() : String(items[0]?.raw.x ?? '') } },
            },
            scales: {
              x: { type: 'linear', title: { display: true, text: this.xColumn ? columnLabel(this.xColumn) : 'Row ordinal' },
                ticks: { maxTicksLimit: 8, ...(timeAxis ? { callback: value => new Date(value).toLocaleString() } : {}) }, grid: { color: '#ebebea' } },
              y: { title: { display: true, text: datasets.map(item => item.label).join(' / ') }, grid: { color: '#ebebea' } },
            },
          },
        });
      } catch (error) { this.chartError = `Chart unavailable: ${error.message}`; }
    },
  },
};
</script>

<style scoped>
.graph-modal { background: #fff; border: 1px solid #deded6; border-radius: 10px; overflow: hidden; }
.graph-header { display: flex; justify-content: space-between; align-items: center; padding: 13px 20px; background: #f6f6f2; color: #18392b; }
.chart-type-toggle { display: flex; border: 1px solid #d0d0c8; border-radius: 6px; overflow: hidden; }
.chart-type-btn { background: #f0f0eb; border: none; padding: 6px 14px; text-transform: capitalize; }
.chart-type-btn.active { background: #18392b; color: white; }
.graph-controls { display: flex; flex-wrap: wrap; gap: 22px; padding: 14px 20px; background: #fafaf7; border-block: 1px solid #e8e8e2; }
.axis-block { display: flex; flex-direction: column; gap: 8px; color: #2196f3; }
.axis-select { border: 1px solid #d0d0c8; border-radius: 5px; padding: 5px 10px; max-width: 100%; }
.y-block { flex: 1; min-width: 0; border: 0; padding: 0; margin: 0; }
.y-block legend { font-size: .85rem; color: #18392b; }
.y-series-list { display: flex; flex-wrap: wrap; gap: 8px; }
.y-series-chip { display: flex; align-items: center; gap: 6px; border: 1px solid; border-radius: 20px; padding: 4px 12px; font-size: .82rem; }
.series-dot { width: 9px; height: 9px; border-radius: 50%; }
.graph-body { height: 380px; position: relative; padding: 16px; }
.graph-empty { text-align: center; color: #666; padding: 80px 10px; }
.graph-footer, .graph-note { font-size: .8rem; color: #666; padding: 10px 20px; margin: 0; background: #fafaf7; }
button, select, input { cursor: pointer; }
.graph-modal { position: fixed; inset: 0; z-index: 11000; border: 0; border-radius: 0; display: flex; flex-direction: column; height: 100vh; height: 100dvh; background: #f4f7f5; }
.graph-header { flex-shrink: 0; padding: 18px 26px; gap: 20px; background: white; border-bottom: 1px solid #dfe7e1; }
.graph-heading, .header-actions { display: flex; align-items: center; gap: 16px; min-width: 0; }
.graph-heading h2 { font-size: 1.15rem; margin: 0 0 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.graph-heading > div { min-width: 0; }
.graph-heading p { margin: 0; color: #64746a; font-size: .82rem; }
.graph-icon { display: grid; place-items: center; width: 44px; height: 44px; flex-shrink: 0; border-radius: 12px; color: #245c40; background: #eaf3ed; font-size: 1.35rem; }
.header-actions { flex-shrink: 0; }
.chart-type-btn { padding: 9px 16px; }
.close-graph { display: flex; align-items: center; gap: 9px; border: 1px solid #d8e1da; background: white; color: #294c37; border-radius: 7px; padding: 9px 12px; }
kbd { color: #6a796f; background: #f0f3f1; font-size: .7rem; }
.graph-workspace { display: flex; flex: 1; min-height: 0; }
.graph-controls { width: 300px; flex: 0 0 300px; min-height: 0; display: flex; flex-direction: column; flex-wrap: nowrap; gap: 22px; padding: 24px 18px; border: 0; border-right: 1px solid #dfe7e1; background: white; overflow: auto; }
.controls-heading h3 { margin: 0 0 6px; font-size: 1rem; color: #18392b; }
.controls-heading p, .controls-hint { font-size: .8rem; color: #6a796f; margin: 0; }
.axis-block { color: #294c37; font-weight: 600; }
.axis-block small { color: #6a796f; font-weight: normal; }
.axis-select { width: 100%; background: #f9fbf9; padding: 10px; }
.y-block { flex: 0 0 auto; }
.y-block legend { display: flex; align-items: center; justify-content: space-between; font-weight: 600; font-size: .9rem; }
.y-block legend span { font-size: .72rem; color: #6a796f; font-weight: normal; }
.series-search { width: 100%; padding: 9px 11px; border: 1px solid #d8e1da; border-radius: 6px; cursor: text; font-size: .83rem; }
.series-actions { display: flex; justify-content: flex-end; margin: 8px 0; }
.series-actions button { background: none; border: 0; color: #47745a; font-size: .77rem; }
.y-series-list { display: flex; flex-direction: column; flex-wrap: nowrap; gap: 5px; }
.y-series-chip { border: 1px solid transparent; border-radius: 7px; padding: 10px; gap: 9px; cursor: pointer; overflow-wrap: anywhere; }
.y-series-chip.selected { background: #edf5ef; border-color: #c6ddcc; }
.y-series-chip:hover { background: #f1f5f2; }
.series-dot { flex-shrink: 0; }
.graph-stage { flex: 1; min-width: 0; min-height: 0; display: flex; flex-direction: column; padding: 24px; gap: 12px; overflow: auto; }
.graph-body { flex: 1 0 280px; min-height: 280px; height: auto; background: white; border: 1px solid #e0e8e2; border-radius: 12px; padding: 20px; }
.graph-empty { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 30px; text-align: center; }
.graph-empty i { font-size: 2.8rem; color: #9ab8a3; margin-bottom: 16px; }
.graph-empty h3 { font-size: 1.15rem; color: #294c37; }
.graph-empty p { max-width: 340px; font-size: .9rem; }
.graph-footer, .graph-note { background: transparent; padding: 0; flex-shrink: 0; line-height: 1.6; }
.graph-note { background: #e9f1eb; padding: 8px 12px; border-radius: 6px; }
:is(button, input, select):focus-visible { outline: 3px solid #7eac8c; outline-offset: 3px; }
@media (max-width: 760px) {
  .graph-header { padding: 12px; gap: 10px; flex-wrap: wrap; }
  .graph-heading { flex: 1; }
  .graph-heading h2 { max-width: 65vw; }
  .header-actions { width: 100%; justify-content: space-between; }
  .graph-workspace { flex-direction: column; overflow: auto; }
  .graph-controls { width: 100%; flex: 0 0 auto; max-height: 35vh; border-right: 0; border-bottom: 1px solid #dfe7e1; padding: 14px; gap: 14px; }
  .controls-heading { display: none; }
  .graph-stage { flex: 1 0 380px; overflow: visible; padding: 12px; }
}
</style>